// TOTP Setup — three-step flow for super_admin accounts.
//
// New super-admin path: the user has no TOTP yet so they don't have a
// main-API session token. We use the pre-auth bootstrap endpoints which
// authenticate via email + password only.
//
// API used:
//   POST /hcm/api/v1/auth/mfa-bootstrap         — { email, password } → { secret, qr_uri, setup_token }
//   POST /hcm/api/v1/auth/mfa-bootstrap-verify  — { setup_token, totp_token } → { access_token, refresh_token, recovery_codes }
//
// Flow used here:
//   1. Page loads → ask for password (the email comes from the URL or admin token).
//   2. Bootstrap → render QR code + manual key.
//   3. User scans, types 6-digit code → bootstrap-verify enables TOTP, returns
//      session tokens AND recovery codes.
//   4. Show recovery codes (copy / download), user confirms saved → done.

import { useEffect, useState } from "react";
import type { JSX, FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { setAccessToken, apiFetch, ApiError } from "../lib/api.js";

interface BootstrapResponse {
  secret: string;
  qr_uri: string;
  setup_token: string;
}

interface BootstrapVerifyResponse {
  access_token: string;
  refresh_token: string;
  user: { id: string; email: string; role: string; tenant_id: string };
  recovery_codes: string[];
}

type Step = "credentials" | "scan" | "confirm" | "save_codes" | "done";

function buildQrUrl(otpauthUrl: string): string {
  return `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(otpauthUrl)}&size=200x200&margin=4`;
}

function buildRecoveryFile(codes: string[], email: string): string {
  return [
    "HCM Admin Recovery Codes",
    "========================",
    `Generated: ${new Date().toISOString()}`,
    `Account:   ${email}`,
    "",
    "Use these codes if you lose access to Google Authenticator.",
    "Each code can only be used ONCE.",
    "",
    ...codes.map((c, i) => `${i + 1}. ${c}`),
    "",
    "Keep this file safe and offline.",
  ].join("\n");
}

// eslint-disable-next-line max-lines-per-function, complexity -- enrollment wizard (QR + verify + recovery download)
export function TotpSetupPage(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // Email arrives in the ?email= query string from the login redirect. With the
  // session JWT now in an httpOnly cookie we can't read it client-side any more.
  const initialEmail = params.get("email") ?? "";

  const [step, setStep] = useState<Step>("credentials");
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [secret, setSecret] = useState("");
  const [qrUri, setQrUri] = useState("");
  const [setupToken, setSetupToken] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  // If somehow we land here with no email, the credentials step prompts for one.
  useEffect(() => {
    if (!email) setStep("credentials");
  }, [email]);

  // eslint-disable-next-line sonarjs/cognitive-complexity -- bootstrap flow handles success/error/lockout/redirect
  async function handleBootstrap(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const data = await apiFetch<BootstrapResponse>("/hcm/api/v1/auth/mfa-bootstrap", {
        method: "POST",
        body:   JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      setSecret(data.secret);
      setQrUri(data.qr_uri);
      setSetupToken(data.setup_token);
      setStep("scan");
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        if (err.status === 400 && (body?.error ?? "").includes("already enabled")) {
          setError("MFA is already set up for this account. Sign in normally instead.");
        } else if (err.status === 401) {
          setError("Invalid email or password.");
        } else {
          setError(typeof body?.error === "string" ? body.error : "Could not start MFA setup.");
        }
      } else {
        setError("Network error. Check your connection.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVerify(): Promise<void> {
    if (totpCode.length !== 6) { setError("Enter the 6-digit code from your authenticator app."); return; }
    setError(null);
    setSubmitting(true);
    try {
      const data = await apiFetch<BootstrapVerifyResponse>("/hcm/api/v1/auth/mfa-bootstrap-verify", {
        method: "POST",
        body:   JSON.stringify({ setup_token: setupToken, totp_token: totpCode }),
      });
      // Hold on to the freshly-issued main-API token so the admin panel can call
      // /api/super-admin/* immediately without going back through login.
      setAccessToken(data.access_token);
      setRecoveryCodes(data.recovery_codes ?? []);
      setStep("save_codes");
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        setError(typeof body?.error === "string" ? body.error : "Invalid code — try again.");
      } else {
        setError("Network error. Check your connection.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  function copyCodes(): void {
    navigator.clipboard.writeText(recoveryCodes.join("\n"))
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })
      .catch(() => { /* clipboard may be unavailable in some browsers */ });
  }

  function downloadCodes(): void {
    const blob = new Blob([buildRecoveryFile(recoveryCodes, email)], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "hcm-admin-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  }

  if (step === "done") {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-md w-full max-w-md p-8 text-center space-y-4">
          <div className="text-4xl">✅</div>
          <h2 className="text-xl font-bold text-slate-900">Two-factor authentication enabled</h2>
          <p className="text-slate-600 text-sm">Your account is now protected. Use Google Authenticator on every sign-in.</p>
          <button
            onClick={() => navigate("/", { replace: true })}
            className="w-full py-2 px-4 rounded-lg bg-indigo-700 text-white font-medium text-sm hover:bg-indigo-800"
          >
            Continue to Admin Panel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-md w-full max-w-lg p-8 space-y-6">
        <div className="text-center">
          <span className="text-2xl font-bold text-indigo-700">HCM Admin</span>
          <h2 className="text-lg font-semibold text-slate-900 mt-2">Set up two-factor authentication</h2>
          <p className="text-sm text-slate-500 mt-1">Required for super-admin access</p>
        </div>

        {step === "credentials" && (
          <form onSubmit={(e) => { void handleBootstrap(e); }} className="space-y-4">
            <p className="text-sm text-slate-600">
              Confirm your password to begin MFA setup. We'll generate a fresh secret tied to your account.
            </p>
            <div>
              <label htmlFor="setup-email" className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                id="setup-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label htmlFor="setup-password" className="block text-sm font-medium text-slate-700 mb-1">Password</label>
              <input
                id="setup-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoFocus
                autoComplete="current-password"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-2 px-4 rounded-lg bg-indigo-700 text-white font-medium text-sm hover:bg-indigo-800 disabled:opacity-50"
            >
              {submitting ? "Starting setup…" : "Continue"}
            </button>
          </form>
        )}

        {step === "scan" && (
          <>
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-800 text-sm">Step 1 — Scan with Google Authenticator</h3>
              <div className="flex justify-center">
                <img
                  src={buildQrUrl(qrUri)}
                  alt="TOTP QR code"
                  className="w-48 h-48 border border-slate-200 rounded-lg bg-white"
                  width={192}
                  height={192}
                />
              </div>
              <p className="text-center text-xs text-slate-500">
                Or enter this key manually:
                <br />
                <code className="inline-block mt-1 bg-slate-100 px-2 py-1 rounded font-mono text-slate-700 text-xs break-all">
                  {secret}
                </code>
              </p>
            </div>
            <button
              onClick={() => { setStep("confirm"); setError(null); }}
              className="w-full py-2 px-4 rounded-lg bg-indigo-700 text-white font-medium text-sm hover:bg-indigo-800"
            >
              I've scanned the code →
            </button>
          </>
        )}

        {step === "confirm" && (
          <>
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-800 text-sm">Step 2 — Enter the 6-digit code to confirm</h3>
              <p className="text-sm text-slate-500">
                Open Google Authenticator and enter the current code for HCM CRM.
              </p>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                autoFocus
                className="w-full text-center text-2xl font-mono tracking-widest rounded-lg border border-slate-300 px-3 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => { setStep("scan"); setError(null); setTotpCode(""); }}
                disabled={submitting}
                className="flex-1 py-2 px-4 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Back
              </button>
              <button
                onClick={() => { void handleVerify(); }}
                disabled={totpCode.length !== 6 || submitting}
                className="flex-1 py-2 px-4 rounded-lg bg-indigo-700 text-white font-medium text-sm hover:bg-indigo-800 disabled:opacity-50"
              >
                {submitting ? "Verifying…" : "Verify & Enable"}
              </button>
            </div>
          </>
        )}

        {step === "save_codes" && (
          <>
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-800 text-sm">
                Step 3 — Save your recovery codes
                <span className="ml-2 text-red-600 font-normal">(shown once only)</span>
              </h3>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-2">
                  {recoveryCodes.map((code) => (
                    <code key={code} className="text-sm font-mono text-slate-700 bg-white border border-slate-200 rounded px-2 py-1 text-center">
                      {code}
                    </code>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={copyCodes}
                  className="flex-1 py-1.5 px-3 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
                >
                  {copied ? "✅ Copied!" : "Copy codes"}
                </button>
                <button
                  onClick={downloadCodes}
                  className="flex-1 py-1.5 px-3 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
                >
                  ⬇ Download
                </button>
              </div>
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">
                ⚠️ Store these codes in a password manager. If you lose your phone,
                use one of these to access your account. Each code works once.
              </p>
            </div>
            <button
              onClick={() => setStep("done")}
              className="w-full py-2 px-4 rounded-lg bg-indigo-700 text-white font-medium text-sm hover:bg-indigo-800"
            >
              I've saved my recovery codes — Done
            </button>
          </>
        )}
      </div>
    </div>
  );
}
